import { PaginatedResponse, useApi } from '@/api';
import {
  CurrencyValue,
  DataTable,
  Flex,
  RelativeDateTime,
  TableSkeleton,
  useTableProps,
  Heading,
  DropdownFilter,
  formatDatetime
} from '@blockanalitica/ui';
import { TableCellBottom, EtherscanLink } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useSearchParams } from '@/hooks';
import { ACTIVITY_EVENT } from '@/constants';

type WalletEvent = {
  tx_hash: string;
  order_index: string;
  datetime: string;
  block_number: number;
  event: string;
  amount: string | number;
  amount_usd: string | number;
  symbol: string;
  debt_symbol: string | null;
};

export default function WalletMarketEventsTable({
  network,
  marketAddress,
  walletAddress
}: {
  network: string;
  marketAddress: string;
  walletAddress: string;
}) {
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });

  const [date] = useSearchParams('date', '');

  const [data, error] = useApi<PaginatedResponse<WalletEvent[]>>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/events/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: filters.filtersApplied['selectedOption'],
      underlying_address: marketAddress,
      date: date
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const fromDate = date ? `on ${formatDatetime(date)}` : '';

  const columns: TableColumn[] = [
    {
      header: 'Event',
      getCell: (row) => row.event
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.amount}
          options={{
            currency: {
              name: row.symbol,
              address: row.underlying_address,
              network: network
            },
            trend: true,
            trendIcon: false
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.amount_usd}>
          <CurrencyValue
            value={row.amount_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount_usd'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <EtherscanLink value={row.tx_hash} network={network} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'order_index'
    }
  ];

  return (
    <Flex flexDirection="column" gap={['m', 's']}>
      <Flex gap={['m', 's']} justifyContent="space-between" alignItems="center">
        <Heading size="2">Events {fromDate}</Heading>
        <DropdownFilter
          options={ACTIVITY_EVENT}
          {...filters}
          withCryptoIcon={false}
        />
      </Flex>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Flex>
  );
}
